<script setup lang="ts">

  import {
    mdiCellphone,
    mdiChevronDown,
    mdiChevronLeft,
    mdiCloseThick,
    mdiCreation,
    mdiLeadPencil,
    mdiLightningBolt
  } from '@mdi/js'
  import MdiIcon from '@/common/components/svg/MdiIcon.vue'
  import { computed, PropType, ref } from 'vue'
  import { AuditLog } from '@/common/graphql/types'
  import AuditLogUser from '@/common/components/auditlog/components/AuditLogUser.vue'
  import AuditLogTimlineItemDisplayValue
    from '@/common/components/auditlog/components/AuditLogTimlineItemDisplayValue.vue'

  const props = defineProps({
    log: {
      type: Object as PropType<AuditLog>,
      required: true
    },
  })

  const expanded = ref(false)

  const icon = computed(() => {
    switch (props.log.action) {
    case 'created':
      return mdiCreation
    case 'updated':
      return mdiLeadPencil
    case 'deleted':
      return mdiCloseThick
    default:
      return mdiLightningBolt
    }
  })

  // changes creates a backwards compatibility layer for old audit logs.
  const changes = computed(() => {
    if (props.log.changes && props.log.changes.length > 0) {
      return props.log.changes
    }

    // Legacy support for old audit logs that don't have changes
    if (props.log.value_old || props.log.value_new) {
      return [{
        id: 1,
        field: props.log.field,
        display_value_old: String(props.log.value_old),
        display_value_new: String(props.log.value_new)
      }]
    }

    return []
  })
</script>

<template>
  <div class="bg-white flex-1 border border-gray-300 shadow-sm rounded-md">
    <div
      class="p-4 rounded-md"
      :class="{'cursor-pointer hover:bg-gray-50': changes.length > 0}"
      @click="expanded = !expanded"
    >
      <div class="flex justify-between">
        <div class="font-semibold flex items-center">
          <div v-if="log.namespace === 'caresuite_app'" class="text-3xs bg-csBlue-100 rounded px-1 py-0.5 mr-2 flex items-center gap-1">
            <MdiIcon :icon="mdiCellphone" class="size-3" />
            App
          </div>
          {{ log.entity_display_name }}
        </div>
        <AuditLogUser v-if="log.user" :user="log.user" />
      </div>
      <div class="text-xs mt-1">
        <div class="flex flex-col xs:flex-row xs:items-center justify-between">
          <div class="text-gray-600 flex items-center gap-1">
            <div
              class="flex items-center gap-1"
              :class="{
                'text-green-500': log.action === 'created',
                'text-red-500': log.action === 'deleted',
                'text-blue-500': log.action === 'updated'
              }"
            >
              <MdiIcon :icon="icon" class="size-4" />
              {{ $t('auditlog.actions.' + log.action) }}
            </div>
            <div>
              &middot;
            </div>
            <div>
              {{ $t(log.entity_type + '.singular') }}
            </div>
          </div>
          <div v-if="changes.length > 0" class="flex items-center text-2xs text-gray-500 mt-1 xs:mt-0">
            <div>
              {{ $t('auditlog.changes', { count: changes.length }) }}
            </div>
            <MdiIcon :icon="expanded ? mdiChevronDown : mdiChevronLeft" class="size-4 mt-px" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="expanded && changes.length > 0" class="p-4 border-t border-gray-100 space-y-1">
      <div v-for="change in changes" :key="change.id" class="flex items-baseline gap-4 text-xs">
        <div class="text-gray-8000 w-52 truncate">
          {{ $t(`${log.entity_type}.fields.${change.field}`) }}
        </div>
        <div class="flex-1 bg-red-50 text-red-800 line-through inline-block px-2 rounded">
          <AuditLogTimlineItemDisplayValue :value="change.display_value_old" />
        </div>
        <div class="flex-1 bg-green-50 text-green-800 inline-block px-2 rounded">
          <AuditLogTimlineItemDisplayValue :value="change.display_value_new" />
        </div>
      </div>
    </div>
  </div>
</template>